/**
 * The ONE place we write to `public.error_logs`. Every capture path
 * (client, server, React boundary, fetch wrapper) funnels here.
 *
 * A failing insert must never throw — it swallows and console-warns.
 * That's a load-bearing rule: observability is not allowed to break
 * the app it observes.
 */

import type { SupabaseClient } from '@supabase/supabase-js'
import { v4 as uuidv4 } from 'uuid'

import { getBreadcrumbs, type Breadcrumb } from './breadcrumbs'
import { getConfig, isMuted, type Feature, type Severity, type UserImpact } from './config'
import { isKnownBrowserNoise } from './noise'
import { normalizeForFingerprint, scrubObject, scrubString } from './sanitize'

export interface InsertParams {
  error: unknown
  severity?: Severity
  feature?: Feature
  userImpact?: UserImpact
  handled?: boolean

  // Correlation
  traceId?: string
  sessionId?: string

  // User context (server-side callers pass these; client fills from auth)
  userId?: string
  proId?: string
  userRole?: string

  // Request context (server-side only)
  url?: string
  method?: string
  statusCode?: number
  responseStatus?: number
  requestBody?: unknown

  // Component context (client-side)
  componentName?: string
  action?: string

  // Free-form debugging context
  metadata?: Record<string, unknown>

  // Rare: skip breadcrumbs (e.g. if caller supplies its own)
  omitBreadcrumbs?: boolean
}

function computeFingerprint(errorType: string | undefined, message: string): string {
  const type = (errorType || 'Error').trim()
  return `${type}::${normalizeForFingerprint(message)}`
}

function truncate(input: string, max: number): string {
  return input.length <= max ? input : input.slice(0, max - 3) + '...'
}

function getBrowserInfo(): { name?: string; version?: string } {
  if (typeof window === 'undefined') return {}
  const ua = window.navigator.userAgent
  if (ua.includes('Firefox')) return { name: 'Firefox', version: ua.match(/Firefox\/(\d+)/)?.[1] }
  if (ua.includes('Edg/')) return { name: 'Edge', version: ua.match(/Edg\/(\d+)/)?.[1] }
  if (ua.includes('Chrome')) return { name: 'Chrome', version: ua.match(/Chrome\/(\d+)/)?.[1] }
  if (ua.includes('Safari')) return { name: 'Safari', version: ua.match(/Version\/(\d+)/)?.[1] }
  return {}
}

/**
 * Insert one error row. Returns void — callers don't need the id back.
 * Never throws.
 */
export async function insertError(supabase: SupabaseClient, params: InsertParams): Promise<void> {
  try {
    if (process.env.NODE_ENV === 'development') {
      // Dev = console-only. Keeps the prod table clean of local noise.
      console.error('[observability][dev]', params.error, params)
      return
    }

    const cfg = getConfig()
    const errObj = params.error instanceof Error ? params.error : new Error(String(params.error))
    const message = truncate(scrubString(errObj.message || 'Unknown error'), 1000)
    const stack = errObj.stack ? truncate(scrubString(errObj.stack, { keepUuids: true }), 5000) : undefined
    const errorType = errObj.name || 'Error'
    const fingerprint = computeFingerprint(errorType, message)

    // Drop known third-party / browser-scanner noise regardless of source.
    // window.onerror already filters here, but explicit clientLogger.error
    // calls from feature code (fetch wrappers, catch blocks) bypassed it —
    // e.g. an Outlook safelink scan surfaced through a component catch as
    // a "real" error and hit the table 3k+ times/week.
    if (isKnownBrowserNoise(message)) return

    if (isMuted(fingerprint)) return

    const browser = getBrowserInfo()
    const breadcrumbs: Breadcrumb[] | undefined = params.omitBreadcrumbs ? undefined : getBreadcrumbs()

    const row = {
      id: uuidv4(),
      message,
      errorStack: stack,
      errorType,
      severity: params.severity || 'error',
      fingerprint,
      release: cfg.release,
      handled: params.handled ?? false,

      // Feature + impact — default to 'unknown' so the fields are never
      // NULL on a fresh insert. Makes the admin viewer's default
      // filter useful and prevents "missing info" rows in the detail
      // modal for anything we haven't explicitly tagged.
      feature: params.feature || 'unknown',
      userImpact: params.userImpact || 'unknown',

      // Correlation
      traceId: params.traceId,
      sessionId: params.sessionId,

      // User context
      userId: params.userId,
      proId: params.proId,
      userRole: params.userRole,
      userTimezone: typeof Intl !== 'undefined' ? Intl.DateTimeFormat().resolvedOptions().timeZone : undefined,

      // Full URL including origin — critical for multi-tenant apps
      // (thriveu.alli.io vs app.alli.io vs www.alli.io) where the
      // pathname alone can't distinguish. Fragment intentionally
      // omitted — Supabase auth callback + /mfa/recover put credentials
      // in the fragment and we must not persist those.
      url:
        params.url ||
        (typeof window !== 'undefined'
          ? window.location.origin +
            window.location.pathname +
            window.location.search
          : undefined),
      method: params.method,
      statusCode: params.statusCode,
      responseStatus: params.responseStatus,
      requestBody: params.requestBody ? scrubObject(params.requestBody) : undefined,

      // Browser info (client-side auto-detect)
      browserName: browser.name,
      browserVersion: browser.version,

      // App context
      appName: cfg.app,
      environment: cfg.environment,
      componentName: params.componentName,
      action: params.action,

      metadata: params.metadata ? scrubObject(params.metadata) : undefined,
      breadcrumbs: breadcrumbs && breadcrumbs.length > 0 ? breadcrumbs : undefined,
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { error } = await (supabase.from('error_logs') as any).insert(row)
    if (error) {
      console.warn('[observability] insert failed:', error)
    }
  } catch (loggingErr) {
    console.warn('[observability] insert threw:', loggingErr)
  }
}
